<template>
  <div class="flex min-h-screen bg-base text-white font-sans">
    <Sidebar />

    <div class="flex-1 flex flex-col mt-20 md:-mt-0 transition-all duration-300">
      
      <!-- Header -->
      <header
        class="mx-4 md:mx-6 mt-4 rounded-2xl bg-gradient-to-b from-[#1a2220]/90 to-[#12181b]/90
               ring-1 ring-[#334D44]/60 backdrop-blur-xl shadow-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4"
      >
        <div class="flex items-center gap-3 w-full md:w-auto">
          <div class="p-2.5 rounded-xl bg-[#b9f1cf]/10 text-[#b9f1cf] shadow-[0_0_15px_rgba(185,241,207,0.15)]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h1 class="text-lg md:text-2xl font-extrabold text-white">مدیریت واریزی‌ها</h1>
            <p class="text-xs text-white/50 mt-0.5">بررسی و تعیین وضعیت تراکنش‌های بانکی</p>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <!-- Filters -->
          <div class="flex bg-[#141a1d] p-1 rounded-xl ring-1 ring-white/10 w-full sm:w-auto">
            <button 
              @click="filterStatus = 'all'"
              :class="['flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all', filterStatus === 'all' ? 'bg-[#b9f1cf] text-[#0a1f14] shadow-lg' : 'text-white/60 hover:text-white']"
            >
              همه
            </button>
            <button 
              @click="filterStatus = 'pending'"
              :class="['flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all', filterStatus === 'pending' ? 'bg-amber-400 text-[#0a1f14] shadow-lg' : 'text-white/60 hover:text-white']"
            >
              در انتظار
            </button>
          </div>

          <!-- Search -->
          <div class="relative w-full sm:w-48">
            <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <i class="fas fa-search text-white/40 text-xs"></i>
            </div>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="جستجو (شناسه/مبلغ)..." 
              class="block w-full p-2.5 pr-9 text-xs rounded-xl bg-white/5 border border-white/10 placeholder-white/40 text-white focus:ring-1 focus:ring-[#b9f1cf] focus:border-[#b9f1cf] outline-none transition" 
            />
          </div>
        </div>
      </header>

      <!-- Content -->
      <main class="p-4 md:p-6 flex-1 overflow-auto">
        
        <!-- Loading -->
        <div v-if="loading" class="grid gap-4">
           <div v-for="i in 5" :key="i" class="h-20 rounded-2xl bg-[#141a1d] animate-pulse ring-1 ring-white/5"></div>
        </div>

        <div v-else>
          <!-- Empty State -->
          <div v-if="filteredDeposits.length === 0" class="flex flex-col items-center justify-center py-20 bg-[#141a1d]/50 rounded-3xl border border-dashed border-white/10">
            <div class="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-3 text-white/20">
              <i class="fas fa-inbox text-2xl"></i>
            </div>
            <p class="text-white/50 text-sm">هیچ واریزی یافت نشد.</p>
          </div>

          <div v-else>
            <!-- Desktop Table -->
            <div class="hidden md:block rounded-2xl bg-[#141a1d]/90 backdrop-blur-xl ring-1 ring-[#334D44]/40 shadow-xl overflow-hidden">
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-white/5">
                  <thead class="bg-white/5">
                    <tr>
                      <th class="px-6 py-4 text-right text-xs font-bold text-[#b9f1cf]">کاربر / شناسه</th>
                      <th class="px-6 py-4 text-center text-xs font-bold text-white/50">مبلغ (تومان)</th>
                      <th class="px-6 py-4 text-center text-xs font-bold text-white/50">تاریخ درخواست</th>
                      <th class="px-6 py-4 text-center text-xs font-bold text-white/50">وضعیت</th>
                      <th class="px-6 py-4 text-center text-xs font-bold text-white/50">فیش</th>
                      <th class="px-6 py-4 text-center text-xs font-bold text-white/50">عملیات</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-white/5">
                    <tr 
                      v-for="item in filteredDeposits" 
                      :key="item._id" 
                      class="group hover:bg-white/[0.02] transition-colors"
                    >
                      <td class="px-6 py-4">
                        <div class="flex flex-col">
                          <span class="text-sm font-bold text-white mb-0.5">UID: {{ item.uid }}</span>
                          <span class="text-[10px] text-white/40 font-mono">{{ item.userid.fname }} {{ item.userid.lname }}</span>
                        </div>
                      </td>
                      
                      <td class="px-6 py-4 text-center">
                        <span class="text-lg font-mono font-bold text-white tracking-tight">{{ formatMoney(item.amount) }}</span>
                      </td>

                      <td class="px-6 py-4 text-center">
                        <div class="flex flex-col items-center">
                          <span class="text-xs text-white/90 mb-0.5">{{ formatDate(item.createdAt).date }}</span>
                          <span class="text-[10px] text-white/40 font-mono">{{ formatDate(item.createdAt).time }}</span>
                        </div>
                      </td>

                      <td class="px-6 py-4 text-center">
                        <span 
                          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold border"
                          :class="getStatusClass(item.status)"
                        >
                          <i class="fas" :class="getStatusIcon(item.status)"></i>
                          {{ getStatusLabel(item.status) }}
                        </span>
                      </td>

                      <td class="px-6 py-4 text-center">
                        <button 
                          v-if="item.img" 
                          @click="showImage(item.img)"
                          class="p-2 rounded-lg bg-white/5 hover:bg-[#b9f1cf] hover:text-[#0a1f14] text-white/60 transition group/btn"
                          title="مشاهده فیش"
                        >
                          <i class="fas fa-image"></i>
                        </button>
                        <span v-else class="text-white/20 text-xs">-</span>
                      </td>

                      <td class="px-6 py-4 text-center">
                        <div class="flex items-center justify-center gap-2" v-if="!item.status || item.status === 'pending'">
                          <button 
                            @click="changeStatus(item, 'approved')"
                            class="p-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-white transition shadow-lg shadow-emerald-500/10"
                            title="تایید"
                          >
                            <i class="fas fa-check"></i>
                          </button>
                          <button 
                            @click="openRejectModal(item)"
                            class="p-2 rounded-lg bg-rose-500/10 hover:bg-rose-500 text-rose-400 hover:text-white transition shadow-lg shadow-rose-500/10"
                            title="رد کردن"
                          >
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                        <span v-else class="text-[10px] text-white/30">تکمیل شده</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Mobile List -->
            <div class="md:hidden grid gap-3">
              <div 
                v-for="item in filteredDeposits" 
                :key="'mob-'+item._id"
                class="rounded-2xl bg-[#141a1d] p-4 ring-1 ring-white/5 relative overflow-hidden"
              >
                <!-- Status Stripe -->
                <div class="absolute top-0 right-0 w-1 h-full" :class="getStatusColor(item.status)"></div>

                <div class="flex justify-between items-start mb-3 pl-2">
                  <div class="flex flex-col">
                    <span class="text-[10px] text-white/40">کد پیگیری</span>
                    <span class="font-mono text-sm text-white font-bold">{{ item.uid }}</span>
                  </div>
                  <span 
                    class="px-2 py-0.5 rounded text-[10px] font-bold border"
                    :class="getStatusClass(item.status)"
                  >
                    {{ getStatusLabel(item.status) }}
                  </span>
                </div>

                <div class="flex justify-between items-center mb-4 bg-white/5 p-3 rounded-xl">
                  <span class="text-xs text-white/60">مبلغ واریزی</span>
                  <div class="text-lg font-black text-white font-mono">
                    {{ formatMoney(item.amount) }} <span class="text-[10px] font-sans font-normal text-white/40">تومان</span>
                  </div>
                </div>

                <div class="flex items-center justify-between gap-3">
                  <button 
                    v-if="item.img"
                    @click="showImage(item.img)"
                    class="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 text-xs font-bold transition flex items-center justify-center gap-2"
                  >
                    <i class="fas fa-image"></i> مشاهده فیش
                  </button>
                  <span v-else class="flex-1 text-center text-[10px] text-white/20">بدون تصویر</span>

                  <div class="flex gap-2" v-if="!item.status || item.status === 'pending'">
                    <button @click="changeStatus(item, 'approved')" class="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-white transition flex items-center justify-center">
                      <i class="fas fa-check"></i>
                    </button>
                    <button @click="openRejectModal(item)" class="w-10 h-10 rounded-xl bg-rose-500/20 text-rose-400 hover:bg-rose-500 hover:text-white transition flex items-center justify-center">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
                
                <div class="mt-3 pt-3 border-t border-white/5 flex justify-between items-center text-[10px] text-white/30">
                  <span>{{ item.userid }}</span>
                  <span class="font-mono">{{ formatDate(item.createdAt).date }} | {{ formatDate(item.createdAt).time }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Reject Modal -->
        <div v-if="rejectModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div class="w-full max-w-sm rounded-2xl bg-[#0f1417] border border-white/10 shadow-2xl overflow-hidden transform transition-all animate-scaleIn">
            <div class="px-5 py-4 border-b border-white/5 flex justify-between items-center bg-[#141a1e]">
              <h3 class="text-sm font-bold text-white">رد درخواست واریز</h3>
              <button @click="closeRejectModal" class="text-white/40 hover:text-white transition">✕</button>
            </div>
            <div class="p-5">
              <p class="text-xs text-white/60 mb-3">لطفاً دلیل رد کردن این واریزی را بنویسید:</p>
              <textarea 
                v-model="rejectReason" 
                rows="4" 
                class="w-full rounded-xl bg-[#1a2220] border border-white/10 focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/50 text-white text-sm p-3 outline-none resize-none transition placeholder-white/20"
                placeholder="مثلاً: عدم تطابق مبلغ فیش با واریزی..."
              ></textarea>
            </div>
            <div class="px-5 py-4 bg-[#141a1e] border-t border-white/5 flex justify-end gap-3">
              <button @click="closeRejectModal" class="px-4 py-2 rounded-xl text-xs font-medium text-white/60 hover:text-white hover:bg-white/5 transition">انصراف</button>
              <button 
                @click="submitReject" 
                :disabled="submitting"
                class="px-6 py-2 rounded-xl text-xs font-bold bg-rose-500 text-white hover:bg-rose-600 shadow-lg shadow-rose-500/20 transition flex items-center gap-2 disabled:opacity-50"
              >
                <span v-if="submitting" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                ثبت و رد
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import Sidebar from '@/components/admin/SidebarAdmin.vue'

const url = process.env.VUE_APP_API_URL || ''
const deposits = ref([])
const loading = ref(true)
const searchQuery = ref('')
const filterStatus = ref('all') // all, pending

// Reject Modal State
const rejectModal = ref(false)
const selectedDeposit = ref(null)
const rejectReason = ref('')
const submitting = ref(false)

onMounted(fetchDeposits)

async function fetchDeposits() {
  loading.value = true
  try {
    const res = await axios.get(`${url}/admin/deposit/all`)
    deposits.value = res.data.data || res.data || []
  } catch (err) {
    Swal.fire({ 
      icon: 'error', 
      title: 'خطا', 
      text: 'دریافت لیست واریزی‌ها با خطا مواجه شد.',
      background: '#1a2220',
      color: '#fff'
    })
  } finally {
    loading.value = false
  }
}

// تغییر وضعیت (تایید)
async function changeStatus(item, status) {
  const confirmResult = await Swal.fire({
    title: status === 'approved' ? 'تایید واریزی' : 'تغییر وضعیت',
    text: `آیا از ${status === 'approved' ? 'تایید' : 'تغییر وضعیت'} این واریزی به مبلغ ${formatMoney(item.amount)} اطمینان دارید؟`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'بله، انجام شود',
    cancelButtonText: 'انصراف',
    background: '#1a2220',
    color: '#fff',
    confirmButtonColor: status === 'approved' ? '#10b981' : '#3b82f6',
    cancelButtonColor: '#374151'
  })

  if (!confirmResult.isConfirmed) return

  await sendStatusUpdate(item._id, status, '')
}

// باز کردن مودال رد
function openRejectModal(item) {
  selectedDeposit.value = item
  rejectReason.value = ''
  rejectModal.value = true
}

function closeRejectModal() {
  rejectModal.value = false
  selectedDeposit.value = null
}

async function submitReject() {
  if (!rejectReason.value.trim()) {
    Swal.fire({ icon: 'warning', text: 'لطفاً دلیل رد را وارد کنید.', background: '#1a2220', color: '#fff' })
    return
  }
  await sendStatusUpdate(selectedDeposit.value._id, 'rejected', rejectReason.value)
  closeRejectModal()
}

// ارسال درخواست به سرور
async function sendStatusUpdate(id, status, textreject) {
  submitting.value = true
  try {
    // اصلاح شده به POST طبق روت بک‌اند شما
    await axios.post(`${url}/admin/deposit/status`, {
      id,
      status,
      textreject
    })

    Swal.fire({
      icon: 'success',
      title: 'موفق',
      text: 'وضعیت واریزی با موفقیت تغییر کرد.',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      background: '#1a2220',
      color: '#fff'
    })

    // آپدیت لوکال برای سرعت بیشتر
    const index = deposits.value.findIndex(d => d._id === id)
    if (index !== -1) {
      deposits.value[index].status = status
      if (textreject) deposits.value[index].textreject = textreject
    }

  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'خطا',
      text: err.response?.data?.error || 'خطایی رخ داد.',
      background: '#1a2220',
      color: '#fff'
    })
  } finally {
    submitting.value = false
  }
}

// Computed
const filteredDeposits = computed(() => {
  let data = deposits.value

  // Filter by Status Tab
  if (filterStatus.value === 'pending') {
    data = data.filter(d => !d.status || d.status === 'pending')
  }

  // Filter by Search
  const q = searchQuery.value.trim().toLowerCase()
  if (q) {
    data = data.filter(d => 
      String(d.uid).includes(q) || 
      String(d.amount).includes(q) ||
      String(d.userid).includes(q)
    )
  }

  // Sort by Date (Newest First)
  return data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
})

// Helpers
function formatMoney(n) {
  return Number(n || 0).toLocaleString('fa-IR')
}

function formatDate(dateStr) {
  if (!dateStr) return { date: '---', time: '' }
  const d = new Date(dateStr)
  return {
    date: d.toLocaleDateString('fa-IR'),
    time: d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
  }
}

function showImage(imgUrl) {
  const fullUrl = imgUrl.startsWith('http') ? imgUrl : `${url}/${imgUrl}`
  Swal.fire({
    imageUrl: fullUrl,
    imageAlt: 'فیش واریزی',
    imageHeight: 400,
    background: '#1a2220',
    color: '#fff',
    showConfirmButton: false,
    showCloseButton: true,
    customClass: { popup: 'rounded-2xl border border-[#334D44]' }
  })
}

function getStatusLabel(status) {
  const map = { 'approved': 'تایید شده', 'rejected': 'رد شده', 'pending': 'در انتظار' }
  return map[status] || 'در انتظار'
}

function getStatusIcon(status) {
  const map = { 'approved': 'fa-check', 'rejected': 'fa-ban', 'pending': 'fa-clock' }
  return map[status] || 'fa-clock'
}

function getStatusClass(status) {
  if (status === 'approved') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
  if (status === 'rejected') return 'bg-rose-500/10 text-rose-400 border-rose-500/20'
  return 'bg-amber-500/10 text-amber-400 border-amber-500/20'
}

function getStatusColor(status) {
  if (status === 'approved') return 'bg-emerald-500'
  if (status === 'rejected') return 'bg-rose-500'
  return 'bg-amber-500'
}
</script>

<style scoped>
/* انیمیشن مودال */
@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-scaleIn { animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1); }

/* اسکرول بار جدول */
.overflow-x-auto::-webkit-scrollbar { height: 6px; }
.overflow-x-auto::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.02); }
.overflow-x-auto::-webkit-scrollbar-thumb { background: #334D44; border-radius: 4px; }
</style>